<!-- src/components/MsgNotification/index.vue -->
<template>
  <div class="message-container">
    <el-badge :value="unreadCount" :max="99" :hidden="unreadCount === 0" class="message-badge">
      <i class="el-icon-bell message-icon" @click="showMessageDialog"></i>
    </el-badge>

    <el-drawer
      title="系统消息"
      :visible.sync="messageDialogVisible"
      direction="rtl"
      size="40%"
      :before-close="handleClose">

      <div class="message-content">
        <div class="message-header">
          <el-button size="small" @click="markAllRead" :disabled="allMessages.length === 0">全部已读</el-button>
        </div>

        <el-tabs v-model="activeTab" @tab-click="handleTabClick">
          <el-tab-pane label="全部消息" name="all">
            <template v-if="allMessages.length === 0">
              <el-empty description="暂无消息"></el-empty>
            </template>
            <template v-else>
              <el-card
                v-for="message in allMessages"
                :key="message.id"
                class="message-card"
                :class="{unread: message && !isMessageRead(message)}"
                @click.native="viewMessage(message)">
                <div class="message-item">
                  <div class="message-title">{{ message.title }}</div>
                  <div class="message-content">{{ message.content }}</div>
                  <div class="message-time">{{ formatTime(message.sendTime) }}</div>
                </div>
              </el-card>
              <el-pagination
                layout="prev, pager, next"
                :total="totalAll"
                :page-size="pageSize"
                :current-page="currentPageAll"
                @current-change="handleAllPageChange"
                class="message-pagination">
              </el-pagination>
            </template>
          </el-tab-pane>

          <el-tab-pane label="未读消息" name="unread">
            <template v-if="unreadMessages.length === 0">
              <el-empty description="暂无未读消息"></el-empty>
            </template>
            <template v-else>
              <el-card
                v-for="message in unreadMessages"
                :key="message.id"
                class="message-card unread"
                @click.native="viewMessage(message)">
                <div class="message-item">
                  <div class="message-title">{{ message.title }}</div>
                  <div class="message-content">{{ message.content }}</div>
                  <div class="message-time">{{ formatTime(message.sendTime) }}</div>
                </div>
              </el-card>
              <el-pagination
                layout="prev, pager, next"
                :total="totalUnread"
                :page-size="pageSize"
                :current-page="currentPageUnread"
                @current-change="handleUnreadPageChange"
                class="message-pagination">
              </el-pagination>
            </template>
          </el-tab-pane>

          <el-tab-pane label="已读消息" name="read">
            <template v-if="readMessages.length === 0">
              <el-empty description="暂无已读消息"></el-empty>
            </template>
            <template v-else>
              <el-card
                v-for="message in readMessages"
                :key="message.id"
                class="message-card"
                @click.native="viewMessage(message)">
                <div class="message-item">
                  <div class="message-title">{{ message.title }}</div>
                  <div class="message-content">{{ message.content }}</div>
                  <div class="message-time">{{ formatTime(message.sendTime) }}</div>
                </div>
              </el-card>
              <el-pagination
                layout="prev, pager, next"
                :total="totalRead"
                :page-size="pageSize"
                :current-page="currentPageRead"
                @current-change="handleReadPageChange"
                class="message-pagination">
              </el-pagination>
            </template>
          </el-tab-pane>
        </el-tabs>
      </div>

      <div class="message-footer">
        <el-button @click="messageDialogVisible = false">关闭</el-button>
      </div>
    </el-drawer>

    <el-dialog
      :visible.sync="messageDetailVisible"
      title="消息详情"
      width="50%">
      <div v-if="currentMessage">
        <el-form label-width="80px">
          <el-form-item label="标题">
            <span>{{ currentMessage.title }}</span>
          </el-form-item>
          <el-form-item label="内容">
            <div v-html="currentMessage.content"></div>
          </el-form-item>
          <el-form-item label="时间">
            <span>{{ formatTime(currentMessage.sendTime) }}</span>
          </el-form-item>
        </el-form>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="messageDetailVisible = false">关闭</el-button>
        <el-button v-if="currentMessage && !isMessageRead(currentMessage)" type="primary"
                   @click="markRead(currentMessage.id)">标记已读</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {getMessagePage, getUnreadMessageCount, markAllAsRead, markAsRead} from '@/api/msg/message'

export default {
  name: 'MessageNotification',
  data() {
    return {
      unreadCount: 0,
      messageDialogVisible: false,
      messageDetailVisible: false,
      allMessages: [],
      unreadMessages: [],
      readMessages: [],
      activeTab: 'all',
      currentMessage: null,
      loading: false,
      timer: null,

      // 分页相关数据
      currentPageAll: 1,
      currentPageUnread: 1,
      currentPageRead: 1,
      pageSize: 5,
      totalAll: 0,
      totalUnread: 0,
      totalRead: 0
    }
  },
  mounted() {
    this.fetchUnreadCount()
    // 每隔30秒检查一次未读消息
    this.timer = setInterval(() => {
      this.fetchUnreadCount()
    }, 30000)
  },
  beforeDestroy() {
    // 清除定时器
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    // 格式化时间
    formatTime(time) {
      // 检查时间值是否存在且有效
      if (!time) return ''

      try {
        // 创建日期对象
        let date
        if (typeof time === 'string') {
          // 处理字符串格式的时间 (如: "2025-07-24 14:44:44")
          if (time.includes('-') && time.includes(':')) {
            // 直接使用字符串，因为它是有效的日期格式
            return time
          }
          // 处理其他字符串格式的时间
          date = new Date(time)
        } else if (typeof time === 'number') {
          // 处理时间戳 (如: 1753339484000)
          date = new Date(time)
        } else if (time instanceof Date) {
          // 已经是日期对象
          date = time
        } else {
          // 其他情况
          return ''
        }

        // 检查日期是否有效
        if (date && !isNaN(date.getTime())) {
          // 格式化日期为 YYYY-MM-DD HH:mm:ss
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          const hours = String(date.getHours()).padStart(2, '0')
          const minutes = String(date.getMinutes()).padStart(2, '0')
          const seconds = String(date.getSeconds()).padStart(2, '0')

          return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
        }

        return ''
      } catch (error) {
        console.error('时间格式化错误:', error)
        return ''
      }
    },
    // 检查消息是否已读
    isMessageRead(message) {
      // 检查消息对象是否存在
      if (!message) return true // 如果消息不存在，认为已读

      // 根据后端返回的数据结构判断消息是否已读
      // 如果消息对象中有read属性，则直接使用
      if (message.hasOwnProperty('readStatus')) {
        return !!message.read // 确保返回布尔值
      }
      // 如果没有read属性，默认认为未读（因为这是未读消息列表）
      return false
    },
    async fetchUnreadCount() {
      try {
        const res = await getUnreadMessageCount()
        this.unreadCount = res.data || 0
      } catch (err) {
        console.error('获取未读消息数失败', err)
      }
    },
    async showMessageDialog() {
      this.messageDialogVisible = true
      this.loading = true
      try {
        // 默认加载全部消息第一页
        await this.fetchAllMessages(1)
        // 加载未读消息第一页
        await this.fetchUnreadMessages(1)
        // 加载已读消息第一页
        await this.fetchReadMessages(1)
      } catch (err) {
        this.$message.error('获取消息失败')
        console.error('获取消息失败', err)
      } finally {
        this.loading = false
      }
    },
    // 获取全部消息
    async fetchAllMessages(page) {
      try {
        const params = {
          pageNum: page,
          pageSize: this.pageSize,
          model: {}
        }
        const res = await getMessagePage(params)
        this.allMessages = res.data.records || []
        this.totalAll = res.data.total || 0
        this.currentPageAll = page
      } catch (err) {
        console.error('获取全部消息失败', err)
      }
    },
    // 获取未读消息
    async fetchUnreadMessages(page) {
      try {
        const params = {
          pageNum: page,
          pageSize: this.pageSize,
          model: {
            readStatus: false
          }
        }
        const res = await getMessagePage(params)
        this.unreadMessages = res.data.records || []
        this.totalUnread = res.data.total || 0
        this.currentPageUnread = page
      } catch (err) {
        console.error('获取未读消息失败', err)
      }
    },
    // 获取已读消息
    async fetchReadMessages(page) {
      try {
        const params = {
          pageNum: page,
          pageSize: this.pageSize,
          model: {
            readStatus: true
          }
        }
        const res = await getMessagePage(params)
        this.readMessages = res.data.records || []
        this.totalRead = res.data.total || 0
        this.currentPageRead = page
      } catch (err) {
        console.error('获取已读消息失败', err)
      }
    },
    handleAllPageChange(page) {
      this.currentPageAll = page
      this.fetchAllMessages(page)
    },
    handleUnreadPageChange(page) {
      this.currentPageUnread = page
      this.fetchUnreadMessages(page)
    },
    handleReadPageChange(page) {
      this.currentPageRead = page
      this.fetchReadMessages(page)
    },
    handleClose(done) {
      done()
    },
    handleTabClick() {
      // 切换标签时的操作
    },
    async markAllRead() {
      try {
        await markAllAsRead()
        this.unreadCount = 0

        // 更新所有标签页中的消息状态
        this.allMessages = this.allMessages.map(msg => {
          return {...msg, readStatus: true}
        })

        this.unreadMessages = []
        this.totalUnread = 0
        this.currentPageUnread = 1

        this.readMessages = [...this.allMessages]
        this.totalRead = this.allMessages.length

        this.$message.success('全部标记为已读')
      } catch (err) {
        this.$message.error('操作失败')
        console.error('标记全部已读失败', err)
      }
    },
    viewMessage(message) {
      // 检查消息对象是否存在
      if (!message) return

      try {

        // 如果消息未读，标记为已读
        if (!this.isMessageRead(message)) {
          this.markRead(message.id)
        }

        this.messageDialogVisible = false;

        // 根据 bizType 跳转到不同的页面
        switch (message.bizType) {
          case 'ORDER_PAID':
            // 跳转到订单列表页面
            this.$router.push({
              path: '/oms/order',
              query: {
                orderSn: message.bizNo
              }
            })
            break

          case 'REFUND_APPLY':
            // 跳转到退款申请列表页面
            this.$router.push({
              path: '/oms/refundApply',
              query: {
                refundSn: message.bizNo
              }
            })
            break

          default:
            // 对于没有特定处理的 bizType，可以跳转到通用的消息列表或显示提示
            this.$message.warning('该消息类型暂不支持跳转')
            break
        }
      } catch (err) {
        this.$message.error('跳转页面失败')
        console.error('跳转页面失败', err)
      }
    },
    async markRead(messageId) {
      try {
        // 移除userId参数
        await markAsRead(messageId)

        // 更新未读数量
        this.unreadCount = Math.max(0, this.unreadCount - 1)

        // 更新所有列表中的消息状态
        this.updateMessageReadStatus(messageId)

        // this.$message.success('标记已读成功')
      } catch (err) {
        this.$message.error('标记已读失败')
        console.error('标记已读失败', err)
      }
    },
    // 更新所有列表中的消息状态
    updateMessageReadStatus(messageId) {
      // 更新全部消息列表
      this.allMessages = this.allMessages.map(msg => {
        if (msg && msg.id === messageId) {
          return {...msg, readStatus: true}
        }
        return msg
      })

      // 更新未读消息列表
      this.unreadMessages = this.unreadMessages.filter(msg => {
        return !(msg && msg.id === messageId)
      })
      this.totalUnread = Math.max(0, this.totalUnread - 1)

      // 更新已读消息列表
      const readMessage = this.allMessages.find(msg => msg.id === messageId)
      if (readMessage) {
        this.readMessages.unshift({...readMessage})
        this.totalRead += 1
      }

      // 更新详情中的消息状态
      if (this.currentMessage && this.currentMessage.id === messageId) {
        this.currentMessage = {...this.currentMessage, readStatus: true}
      }
    }
  }
}
</script>

<style scoped>
.message-container {
  display: inline-block;
  margin-right: 30px;
}

.message-icon {
  font-size: 22px;
  color: #409eff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.message-icon:hover {
  color: #409eff;
}

.message-badge ::v-deep .el-badge__content {
  background-color: #f56c6c;
  border: none;
}

.message-content {
  padding: 0 20px;
  height: calc(100% - 60px);
}

.message-header {
  text-align: right;
  margin-bottom: 15px;
}

.message-card {
  margin-bottom: 10px;
  cursor: pointer;
}

.message-card.unread {
  border-left: 4px solid #409eff;
  background-color: #ecf5ff;
}

.message-item {
  padding: 10px;
}

.message-title {
  font-weight: bold;
  margin-bottom: 5px;
}

.message-time {
  font-size: 12px;
  color: #999;
  margin-bottom: 5px;
}

.message-content {
  font-size: 14px;
  color: #666;
  /* 支持换行显示 */
  white-space: pre-line;
  word-wrap: break-word;
  line-height: 1.5; /* 增加行高提高可读性 */
}

.message-footer {
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: 10px 20px;
  text-align: right;
  box-sizing: border-box;
  border-top: 1px solid #eee;
}

.message-pagination {
  text-align: center;
  margin-top: 20px;
}
</style>




// WEBPACK FOOTER //
// src/components/MsgNotification/index.vue